import { DatePicker, Select } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getSemesterMonths, getSemesters } from "../../api/semesters.api";
import { getELearningStudents, getELearningSummary } from "../../api/statistics.api";
import Button from "../../components/Button";
import Checkbox from "../../components/Checkbox";
import SimpleHeader from "../../components/layout/SimpleHeader";
import SelectCampus from "../../components/SelectCampus";
import List from "../../components/statistics/e-learning/List";
import SelectLevel from "../../components/statistics/e-learning/SelectLevel";
import Summary from "../../components/statistics/e-learning/Summary";
import MultiCampusModal from "../../components/statistics/regular-test/modals/MultiCampusModal";
import TabItem from "../../components/statistics/TabItem";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import config from "../../lib/config";
import { IGetSemesterMonths, IGetSemesters } from "../../models/semesters.model";
import { IGetELearningStudents, IGetELearningSummary } from "../../models/statistics.model";
import { RootState } from "../../store/rootReducer";
import useLoader from "../../hooks/useLoader";
import EduStepSelect from "../../components/common/EduStepSelect";
import TableEmpty from "../../components/common/TableEmpty";
import SelectCampusMultiChoice from "../../components/SelectCampusMultiChoice";

enum Contents {
  Summary,
  List,
};

const StatisticsELearning = () => {
  const { t } = useTranslation();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesters>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetELearningSummary>();
  const { data: res2Month, execute: execute2M } = useAxios<IGetSemesterMonths>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetELearningStudents>();
  useLoader(
    isLoading || isLoading2 || isLoading3,
    [error, error2, error3],
  );

  const [content, setContent] = useState(Contents.Summary);
  const [semesterCode, setSemesterCode] = useState<number>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [courseCode, setCourseCode] = useState<string>();
  const [learningTypeCode, setLearningTypeCode] = useState<string>();
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [exceptRetire, setExceptRetire] = useState(false);

  useEffect(() => {
    execute(getSemesters());

    defSelectBoxSetting();
  }, []);

  const defSelectBoxSetting = () => {
    //검색 필드 Default값 셋팅
    const defSelValue:any = "";
    setEduStepCode(defSelValue);
    setCourseCode(defSelValue);
    setLearningTypeCode(defSelValue);
  }

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    if (content === Contents.List && !courseCode) {
      setCourseCode(undefined);
    }

    getELearningData(1);
  }, [content]);

  useEffect(() => {
    if (isFirstRun) {
      return;
    }

    getELearningData();
  }, [pageNo]);

  const getELearningData = (_pageNo?: number) => {
    if (!semesterCode) {
      return infoNotification(t('statistics.dttt.message1'));
    }
    if (content === Contents.List && !courseCode) {
      return infoNotification(t('statistics.eLearning.2'));
    }
    if (content === Contents.List && !learningTypeCode) {
      return infoNotification(t('statistics.eLearning.3'));
    }

    const req: any = {
      semesterCode,
      courseCode,
      learningTypeCode: learningTypeCode
        ? [learningTypeCode]
        : ['PP', 'RV'],
      campusCode: content === Contents.Summary
        ? auth.hqYn === 'Y'
          ? campusCodes
          : (campusCodes.length ? campusCodes : auth.campus)
        : currentCampus,
      exceptRetireYn: exceptRetire ? 'Y' : 'N'
    };
    if (eduStepCode) {
      req.eduStepCode = eduStepCode;
    }

    switch (content) {
      case Contents.Summary:
        if (semesterCode) {
          execute2M(getSemesterMonths(String(semesterCode)));
        }
        execute2(getELearningSummary(req));
        break;
      case Contents.List:
        req.perPage = config.pageSize;
        req.pageNo = _pageNo || pageNo;
        execute3(getELearningStudents(req));
        break;
    }
  }

  const renderContent = () => {
    switch (content) {
      case Contents.Summary:
        return (
          <Summary
            rawData={res2?.data}
            rawData2={res2Month?.data}
          />
        );
      case Contents.List:
        return (
          <List
            res={res3}
            pageNo={pageNo}
            setPageNo={setPageNo}
            semesterCode={semesterCode}
            courseCode={courseCode}
            learningTypeCode={learningTypeCode}
            exceptRetire={exceptRetire}
          />
        );
    }
  }

  return (
    <>
    <SimpleHeader
      iconFile="icon-statistics-big.svg"
      parentMenu={t('common.menu.statistics')}
      currentMenu={t('statistics.eLearning.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
            {
              content === Contents.Summary
                ? (
                  <>
                    <SelectCampusMultiChoice
                      placeholder={t('statistics.eLearning.title2')}
                      onChange={setCampusCodes}
                      value={campusCodes}
                    />
                    <label>
                      ({campusCodes.length ? `${campusCodes.length}${t('statistics.attendance.text1')}` : t('common.text.all')})
                    </label>
                  </>
                  )
                : <div className="w-150p"><SelectCampus /></div>
            }
          </li>
          <li>
            <label>{t('class.class.placeholder4')}</label> {/* 학기 */}
            <Select
              placeholder={t('statistics.regularTest.placeholder2')}
              value={semesterCode}
              onChange={value => setSemesterCode(value)}
            >
              {res?.data?.filter(item => item.semesterType === 'RG').map(item => (  
                <Select.Option
                  key={item.semesterCode}
                  value={item.semesterCode}
                >
                  {item.semesterName}
                </Select.Option>
              ))}
            </Select>
          </li>
          <li>
            <label>{t('common.placeholder.1')}</label> {/* 교육단계 */}
            <EduStepSelect
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);
                if (content === Contents.List) {
                  setCourseCode(undefined);
                } else {
                  const defSelValue:any = "";
                  setCourseCode(defSelValue);
                }
              }}
              includeAll
            />
          </li>
          <li>
            <label>{t('student.student.column26')}</label> {/* 레벨 */}
            <div>
              <SelectLevel
                courseCode={courseCode}
                setCourseCode={setCourseCode}
                includeAll={content === Contents.Summary}
                eduStepCode={eduStepCode}
                placeholder={t('statistics.regularTest.placeholder2')}
              />
            </div>
          </li>
          <li>
            <label>{t('statistics.regularTest.placeholder1')}</label> {/* 유형 */}
            <Select
              placeholder={t('statistics.eLearning.column1')}
              value={learningTypeCode}
              onChange={value => setLearningTypeCode(value)}
            >
              {(content === Contents.Summary) && (
                <Select.Option value="">{t('common.text.all')}</Select.Option>
              )}
              <Select.Option value="PP">iCoaching</Select.Option>
              <Select.Option value="RV">e-Learning</Select.Option>
            </Select>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={() => getELearningData(1)}
                text={t('common.button.search')}
              />
            </div>
          </li>
          <li>
            <label>&nbsp;</label>
            <Checkbox
              checked={exceptRetire}
              onChange={() => setExceptRetire(!exceptRetire)}
              text={t('퇴원생 제외')}
            />
          </li>
        </ul>
      </div>
    </div>	
    <div className="content scrollbar">
      {
        res2?.data || res3?.data
          ? (
            <>
              <div className="comm-tab__line">
                <ul>
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Summary}
                    tabLabel={t('statistics.attendance.Tab1')}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.List}
                    tabLabel={t('statistics.homework.tab1')}
                  />
                </ul>
              </div>
              <div>
                {renderContent()}
              </div>
            </>
            )
          : isLoading2 || isLoading3
            ? null
            : (
              <TableEmpty text={t('statistics.appday.text1')} />
              )
      }

      {multiCampusVisible &&
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }
    </div>
    </>
  );
}

export default StatisticsELearning;
